import { useSortable } from "@dnd-kit/sortable"
import { CSS as DnDCSS } from "@dnd-kit/utilities"
import { useMergeRefs } from "@floating-ui/react"
import classNames from "classnames"
import {
  useCallback,
  forwardRef,
  useRef,
} from "react"
import type {
  PointerEventHandler,
  ComponentPropsWithoutRef,
  Ref,
} from "react"

import styles from "./SortableList.module.scss"

export interface Props extends ComponentPropsWithoutRef<"button"> {
  id: string;
}

/*
 * an item that can be sorted using drag and drop behavior which also
 * supports onClick events
 */
export const SortableListItem = forwardRef<HTMLButtonElement, Props>(
  function SortableListItem(props: Props, propsRef: Ref<HTMLButtonElement>) {
    const internalRef = useRef<HTMLButtonElement>(null)

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: props.id,
      disabled: props.disabled,
    })

    const ref = useMergeRefs<HTMLButtonElement>([
      propsRef,
      setNodeRef,
      internalRef,
    ])

    const style = {
      transform: DnDCSS.Transform.toString(transform),
      transition,
    }

    const className = classNames(
      styles.sortableListItem,
      props.className,
    )

    // this will handle the onClick action, it is not fired for drag events
    // because drag events will use the OverlayItem
    const onPointerUp: PointerEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (props.onClick) {
          props.onClick(e)
        }
      },
      [
        internalRef,
        props.onClick,
      ],
    )

    return (
      <button
        {...props}
        onClick={undefined}
        ref={ref}
        style={style}
        className={className}
        onPointerUp={onPointerUp}
        {...attributes}
        {...listeners}
      />
    )
  },
)
